/**
 * One vocabulary for collections, shared by every surface that talks about them.
 *
 * `CollectionsScreen` and `SearchPageScreen` each grew their own private copy of KIND_LABELS,
 * GLOBAL_KINDS, VIEWER_KINDS and `rpcMissing`. That was survivable at two copies and is not at
 * three — and the copies had ALREADY drifted from the database: neither knew about `underground`
 * or `neighborhood`, which have been live in `search_guide_venue_ids` since migration
 * 20260870000000 and are live on three rows in `search_guides` right now ("Underground",
 * "Uptown", "Downtown"). Both screens rendered them as the raw slug.
 *
 * ⚠️ ADDING A KIND IS A THREE-PLACE EDIT: `search_guide_venue_ids` (the rule), the whitelist in
 * `admin_upsert_search_guide` (the validator), and this file (the words). Miss the second and the
 * row becomes uneditable from the dashboard — see §8 of
 * `supabase/migrations/20260878000000_collection_detail_editing.sql`, which is exactly that bug.
 */

/** Kinds whose list is one list worldwide; everything else resolves against a city. */
export const GLOBAL_KINDS = new Set(['trending_global', 'most_rated', 'best_global', 'divisive']);

/**
 * ⚠️ `friends` resolves through `auth.uid()` inside `search_guide_venue_ids`. From an admin session
 * it returns the ADMIN's mutual follows, or — with no JWT at all — nothing. There is no answer
 * that is true for everybody, so no surface may render it as a count or as an empty collection.
 * Say "per viewer" instead.
 */
export const VIEWER_KINDS = new Set(['friends']);

/** Kinds that carry no rule inputs at all — everything about them is in the kind itself. */
export const RULELESS_KINDS = new Set([
  'best_city',
  'trending',
  'trending_global',
  'late_night',
  'classics',
  'new_here',
  'most_rated',
  'best_global',
  'divisive',
  'friends',
  'underground',
]);

export const KIND_LABELS: Record<string, string> = {
  manual: 'Hand-picked',
  best_city: 'Best in city',
  trending: 'Trending in city',
  trending_global: 'Trending worldwide',
  tag: 'Thought tag',
  category: 'Category',
  criterion: 'Criterion',
  late_night: 'Late night',
  classics: 'Classics',
  new_here: 'Newly rated',
  most_rated: 'Most rated',
  best_global: 'Highest rated',
  divisive: 'Divisive',
  friends: 'Friends',
  underground: 'Underground',
  neighborhood: 'Neighbourhood box',
};

/** One line describing what the rule actually tests — read off the live function body, not guessed. */
export const KIND_RULES: Record<string, string> = {
  manual: 'Exactly the venues listed below, in the order they are listed.',
  best_city: 'Every rated venue in the reader’s city, best first.',
  trending: 'Rated in the reader’s city and logged at least once in the last 30 days.',
  trending_global: '3+ raters anywhere, and logged in the last 45 days.',
  tag: 'Any log at the venue carries this thought tag. Only 190 venues in the whole corpus carry any tag.',
  category: 'The venue’s Foursquare category is one of these, in the reader’s city.',
  criterion: 'This criterion scores at least 0.4 above the corpus average for it, in the reader’s city.',
  late_night: 'Average logged peak time is 24 or later on the midnight-anchored clock (24 = midnight, 26 = 2am).',
  classics: 'Foursquare rating 8.0+, in the reader’s city, excluding theatres and stadiums.',
  new_here: 'First rated in the last 30 days, score 70+, in the reader’s city.',
  most_rated: 'Ordered purely by distinct rater count. No floor — that is the whole rule.',
  best_global: '5+ raters anywhere, ordered by raw Shado score.',
  divisive: '3+ raters and a score spread of 0.75 or more.',
  friends: 'Rated by people the READER mutually follows. Different for every reader.',
  underground: 'Exactly 1 rater and a score of 70+ — deliberately the set every other collection’s 2-rater floor excludes.',
  neighborhood: 'Inside a lat/lng rectangle stored on the collection. There is no neighbourhood column on venues; the box is the whole mechanism.',
};

/**
 * The five criteria that are actually recorded on a log. `door`, `vibe`, `crowd`, `sound` and
 * `dancefloor` are columns on `logs` but are NULL on every live row, so a criterion guide pointed
 * at one would be permanently empty — `admin_upsert_search_guide` rejects them for that reason.
 */
export const CRITERION_TAGS = ['drinks', 'atmosphere', 'music', 'service', 'lineup'] as const;

/** The app drops any collection that cannot fill this many venues in the reader's city. */
export const MIN_VENUES = 4;

/**
 * A function PostgREST could not find, rather than a real failure. Comes back as PGRST202 /
 * SQLSTATE 42883. Callers turn this into a quiet set-up note, never a red banner — the
 * `CityOverviews` pattern.
 */
export function rpcMissing(message: string, fn: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes(fn.toLowerCase()) ||
    m.includes('42883') ||
    m.includes('pgrst202') ||
    m.includes('could not find the function') ||
    m.includes('does not exist')
  );
}

export function errText(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

/** Mirrors `admin_list_search_guides`. Kept here so the table and the detail panel cannot drift. */
export type GuideRow = {
  id: string;
  title: string;
  subtitle: string | null;
  kind: string;
  city_id: string | null;
  city_name: string | null;
  tag: string | null;
  categories: string[] | null;
  venue_ids: string[] | null;
  sort_order: number;
  is_active: boolean;
  venue_count: number;
  scope_city_id?: string | null;
  cover_venue_id?: string | null;
  cover_venue_name?: string | null;
  cover_photo_url?: string | null;
  cover_in_scope?: boolean;
  effective_cover_venue_id?: string | null;
  effective_cover_venue_name?: string | null;
  effective_cover_photo_url?: string | null;
  renders?: boolean;
};

/** One option in the cover picker — `admin_search_guide_cover_options`. */
export type CoverOption = {
  venue_id: string;
  name: string | null;
  photo_url: string | null;
  score: number | null;
  rater_count: number;
  position: number;
};

/** True when this collection's list follows the READER's city rather than being fixed. */
export function isCityScoped(kind: string, cityId: string | null): boolean {
  return !GLOBAL_KINDS.has(kind) && !VIEWER_KINDS.has(kind) && !cityId;
}
